/**
 * Cubic-bezier easing editor: the curve of the selected keyframes' easing with two draggable control points
 * (x clamped to 0..1 as in CSS; y free, so overshoot works), plus the numeric values.
 */
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { bezierOf } from '@prism-graphics/runtime';
import type { Easing } from '@prism-graphics/scene-schema';
import { t } from '../i18n.ts';

const SIZE = 168;
const PAD = 16;
const INNER = SIZE - 2 * PAD;
/** Headroom above and below the unit box for overshooting curves (y from about -0.6 to 1.6). */
const EXTRA = Math.round(INNER * 0.6);
const HEIGHT = INNER + 2 * EXTRA + 2 * PAD;
const toX = (x: number) => PAD + x * INNER;
const toY = (y: number) => PAD + EXTRA + (1 - y) * INNER;

type Bezier = [number, number, number, number];

export function CurveEditor({ easing, onChange, onCommit }: { easing: Easing | undefined; onChange: (b: Bezier) => void; onCommit: () => void }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<1 | 2 | undefined>();
  const b = (bezierOf(easing) ?? [0, 0, 1, 1]) as Bezier;
  const round = (n: number) => Math.round(n * 1000) / 1000;

  const point = (e: ReactPointerEvent): { x: number; y: number } => {
    const r = svgRef.current!.getBoundingClientRect();
    const sx = ((e.clientX - r.left) / r.width) * SIZE;
    const sy = ((e.clientY - r.top) / r.height) * HEIGHT;
    return { x: Math.min(1, Math.max(0, (sx - PAD) / INNER)), y: Math.min(2, Math.max(-1, 1 - (sy - PAD - EXTRA) / INNER)) };
  };
  const onMove = (e: ReactPointerEvent) => {
    if (!drag) return;
    const p = point(e);
    const next: Bezier = drag === 1 ? [round(p.x), round(p.y), b[2], b[3]] : [b[0], b[1], round(p.x), round(p.y)];
    onChange(next);
  };
  const path = `M ${toX(0)} ${toY(0)} C ${toX(b[0])} ${toY(b[1])}, ${toX(b[2])} ${toY(b[3])}, ${toX(1)} ${toY(1)}`;
  return (
    <div className="curve-editor" data-testid="curve-editor">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SIZE} ${HEIGHT}`}
        width={SIZE}
        height={HEIGHT}
        role="group"
        aria-label={t('timeline.curve.label', { value: b.join(', ') })}
        onPointerMove={onMove}
        onPointerUp={(e) => {
          if (drag) onCommit();
          setDrag(undefined);
          if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
        }}
      >
        <rect className="ce-box" x={PAD} y={PAD + EXTRA} width={INNER} height={INNER} />
        <line className="ce-diag" x1={toX(0)} y1={toY(0)} x2={toX(1)} y2={toY(1)} />
        <line className="ce-arm" x1={toX(0)} y1={toY(0)} x2={toX(b[0])} y2={toY(b[1])} />
        <line className="ce-arm" x1={toX(1)} y1={toY(1)} x2={toX(b[2])} y2={toY(b[3])} />
        <path className="ce-curve" d={path} />
        {([1, 2] as const).map((h) => (
          <circle
            key={h}
            className={`ce-handle${drag === h ? ' active' : ''}`}
            data-testid={`curve-handle-${h}`}
            cx={toX(h === 1 ? b[0] : b[2])}
            cy={toY(h === 1 ? b[1] : b[3])}
            r={6}
            tabIndex={0}
            role="button"
            aria-label={t('timeline.curve.handle', { n: h, x: round(h === 1 ? b[0] : b[2]), y: round(h === 1 ? b[1] : b[3]) })}
            onKeyDown={(e) => {
              // Keyboard: the arrow keys move the control point by 0.01 (Shift: 0.1).
              const step = e.shiftKey ? 0.1 : 0.01;
              const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] }[e.key];
              if (!d) return;
              e.preventDefault();
              e.stopPropagation();
              const [x, y] = h === 1 ? [b[0], b[1]] : [b[2], b[3]];
              const nx = round(Math.min(1, Math.max(0, x + d[0]!)));
              const ny = round(Math.min(2, Math.max(-1, y + d[1]!)));
              onChange(h === 1 ? [nx, ny, b[2], b[3]] : [b[0], b[1], nx, ny]);
              onCommit();
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              svgRef.current?.setPointerCapture(e.pointerId);
              setDrag(h);
            }}
          />
        ))}
      </svg>
      <div className="ce-values" aria-live="polite">
        cubic-bezier({b.map((v) => round(v)).join(', ')})
      </div>
    </div>
  );
}
